/**
 * MindMapCanvas Component
 * React Flow canvas with drag-drop and node connections
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  Panel,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Connection,
  ConnectionMode,
  BackgroundVariant,
  OnNodesChange,
  OnEdgesChange,
  SelectionMode,
  FinalConnectionState,
  Node,
  Edge,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import LinkIcon from "@mui/icons-material/Link";
import { useColorMode } from "../../../contexts/AppThemeProvider";
import { useDnD } from "./DnDContext";
import MindMapNode from "./MindMapNode";
import BrokenEdge from "./BrokenEdge";
import SmartEdge from "./SmartEdge";
import {
  MindMapNode as MindMapNodeType,
  MindMapEdge,
  MindMapNodeType as NodeType,
} from "./mindMapTypes";
import { generateNodeId } from "./mindMapUtils";
import { goalAPI, debtAPI, expenseAPI, incomeAPI } from "../../../services/api";
import { housingForecastProfilesDB } from "../../../services/database/housingForecastProfilesDB";
import { taxProfilesDB } from "../../../services/database/taxProfilesDB";
import { calculateIncomeDetails } from "../../../lib/taxCalculatorUtils";
import { calculateHousingSaleAnalysis } from "../../../lib/housingForecastUtils";
import { Debt, Expense, Goal, Income } from "../../../lib/types";

const nodeTypes = {
  topic: MindMapNode,
  idea: MindMapNode,
  note: MindMapNode,
  goal: MindMapNode,
  step: MindMapNode,
  category: MindMapNode,
  break: MindMapNode,
  junction: MindMapNode,
};

const edgeTypes = {
  broken: BrokenEdge,
  smart: SmartEdge,
};

interface MindMapCanvasProps {
  initialNodes: MindMapNodeType[];
  initialEdges: MindMapEdge[];
  onNodesChange: (nodes: MindMapNodeType[]) => void;
  onEdgesChange: (edges: MindMapEdge[]) => void;
  profileId?: string;
}

const MindMapCanvas: React.FC<MindMapCanvasProps> = ({
  initialNodes,
  initialEdges,
  onNodesChange,
  onEdgesChange,
  profileId,
}) => {
  const { mode } = useColorMode();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState(initialEdges);
  const { screenToFlowPosition, getViewport, setViewport } = useReactFlow();
  useDnD();
  const viewportInitialized = useRef(false);
  const viewportStorageKey = useMemo(() => {
    if (profileId) return `mindmap_viewport_v1:profile:${profileId}`;
    return "mindmap_viewport_v1:global";
  }, [profileId]);
  const viewportGlobalKey = "mindmap_viewport_v1:global";
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeType, setSelectedNodeType] = useState<NodeType | null>(
    null
  );
  const [linksOpen, setLinksOpen] = useState(false);
  const [alignmentGuides, setAlignmentGuides] = useState<{
    x: number[];
    y: number[];
  }>({ x: [], y: [] });
  const [linksTab, setLinksTab] = useState<
    "goal" | "debt" | "expense" | "income" | "housingForecast"
  >("goal");
  const [linksQuery, setLinksQuery] = useState("");
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksLoadedOnce, setLinksLoadedOnce] = useState(false);
  const [availableGoals, setAvailableGoals] = useState<Goal[]>([]);
  const [availableDebts, setAvailableDebts] = useState<Debt[]>([]);
  const [availableExpenses, setAvailableExpenses] = useState<Expense[]>([]);
  const [availableIncome, setAvailableIncome] = useState<Income[]>([]);
  const [availableHousing, setAvailableHousing] = useState<
    Array<{ id: string; name: string; settings: any }>
  >([]);
  const [availableTaxProfiles, setAvailableTaxProfiles] = useState<
    Array<{ id: string; incomeInfo: any }>
  >([]);
  const [housingExcludeTaxById, setHousingExcludeTaxById] = useState<
    Record<string, boolean>
  >({});
  const edgeUpdateSuccessful = useRef(true);
  const migratedBreaks = useRef(false);

  const isDark = mode === "dark";
  const edgeColor = isDark ? "#5dc4b2" : "#2bb39d";
  const draggingEdgeColor = "#ff0000";
  const SNAP_PX = 10;
  const [draggingNodeIds, setDraggingNodeIds] = useState(new Set<string>());
  const defaultMarkerEnd = useMemo(
    () => ({
      type: MarkerType.ArrowClosed,
      color: edgeColor,
    }),
    [edgeColor]
  );

  // Ensure all edges render with an arrow marker so directionality is obvious.
  const edgesWithMarkers = useMemo(() => {
    const hasDraggingNode = draggingNodeIds.size > 0;
    return edges.map((e) => {
      const nextType =
        !e.type || e.type === "smoothstep" ? ("smart" as const) : e.type;
      const isConnectedToDragged =
        hasDraggingNode &&
        (draggingNodeIds.has(e.source) || draggingNodeIds.has(e.target));

      return {
        ...e,
        type: nextType,
        markerEnd: e.markerEnd ?? defaultMarkerEnd,
        style: isConnectedToDragged
          ? {
              ...(e.style || {}),
              stroke: draggingEdgeColor,
              opacity: 1,
            }
          : e.style,
      };
    });
  }, [defaultMarkerEnd, draggingEdgeColor, draggingNodeIds, edges]);

  const markNodeDragging = useCallback((nodeId: string) => {
    setDraggingNodeIds((prev) => {
      if (prev.has(nodeId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(nodeId);
      return next;
    });
  }, []);

  const clearNodeDragging = useCallback((nodeId: string) => {
    setDraggingNodeIds((prev) => {
      if (!prev.has(nodeId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(nodeId);
      return next;
    });
  }, []);

  // Restore viewport from URL on mount
  useEffect(() => {
    if (viewportInitialized.current) return;

    const params = new URLSearchParams(window.location.search);
    const zoom = params.get("zoom");
    const x = params.get("x");
    const y = params.get("y");

    if (zoom && x && y) {
      // Delay to ensure React Flow is ready
      setTimeout(() => {
        setViewport({
          zoom: parseFloat(zoom),
          x: parseFloat(x),
          y: parseFloat(y),
        });
        viewportInitialized.current = true;
      }, 100);
    } else {
      // No URL state; restore from persisted viewport.
      try {
        const raw =
          localStorage.getItem(viewportStorageKey) ||
          localStorage.getItem(viewportGlobalKey);
        if (raw) {
          const parsed = JSON.parse(raw) as {
            zoom?: number;
            x?: number;
            y?: number;
          };
          const zoom = Number(parsed.zoom);
          const x = Number(parsed.x);
          const y = Number(parsed.y);
          if (
            Number.isFinite(zoom) &&
            Number.isFinite(x) &&
            Number.isFinite(y)
          ) {
            setTimeout(() => {
              setViewport({
                zoom,
                x,
                y,
              });
              viewportInitialized.current = true;
            }, 100);
            return;
          }
        }
      } catch {
        // ignore
      }
      viewportInitialized.current = true;
    }
  }, [setViewport, viewportGlobalKey, viewportStorageKey]);

  // Save viewport to URL when user stops moving
  const handleMoveEnd = useCallback(() => {
    const viewport = getViewport();
    const url = new URL(window.location.href);
    url.searchParams.set("zoom", viewport.zoom.toFixed(2));
    url.searchParams.set("x", viewport.x.toFixed(0));
    url.searchParams.set("y", viewport.y.toFixed(0));
    window.history.replaceState({}, "", url.toString());
    try {
      const payload = JSON.stringify({
        zoom: viewport.zoom,
        x: viewport.x,
        y: viewport.y,
      });
      localStorage.setItem(viewportGlobalKey, payload);
      localStorage.setItem(viewportStorageKey, payload);
    } catch {
      // ignore
    }
  }, [getViewport, viewportGlobalKey, viewportStorageKey]);

  // Persist viewport on unmount (covers navigate-away without moving).
  useEffect(() => {
    return () => {
      try {
        const viewport = getViewport();
        const payload = JSON.stringify({
          zoom: viewport.zoom,
          x: viewport.x,
          y: viewport.y,
        });
        localStorage.setItem(viewportGlobalKey, payload);
        localStorage.setItem(viewportStorageKey, payload);
      } catch {
        // ignore
      }
    };
  }, [getViewport, viewportGlobalKey, viewportStorageKey]);

  // Undo/Redo history
  interface HistoryState {
    nodes: MindMapNodeType[];
    edges: MindMapEdge[];
  }
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedo = useRef(false);
  const historyTimeout = useRef<NodeJS.Timeout | null>(null);

  // Save state to history (debounced)
  const saveToHistory = useCallback(() => {
    if (isUndoRedo.current) return;

    if (historyTimeout.current) {
      clearTimeout(historyTimeout.current);
    }

    historyTimeout.current = setTimeout(() => {
      setHistory((prev) => {
        const newHistory = prev.slice(0, historyIndex + 1);
        const currentState = { nodes: [...nodes], edges: [...edges] };
        // Don't add if same as last state
        const lastState = newHistory[newHistory.length - 1];
        if (
          lastState &&
          JSON.stringify(lastState) === JSON.stringify(currentState)
        ) {
          return prev;
        }
        return [...newHistory, currentState].slice(-50); // Keep last 50 states
      });
      setHistoryIndex((prev) => Math.min(prev + 1, 49));
    }, 300);
  }, [nodes, edges, historyIndex]);

  // Undo action
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;

    isUndoRedo.current = true;
    const newIndex = historyIndex - 1;
    const state = history[newIndex];
    if (state) {
      setNodes(state.nodes);
      setEdges(state.edges);
      setHistoryIndex(newIndex);
    }
    setTimeout(() => {
      isUndoRedo.current = false;
    }, 100);
  }, [history, historyIndex, setNodes, setEdges]);

  // Redo action
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;

    isUndoRedo.current = true;
    const newIndex = historyIndex + 1;
    const state = history[newIndex];
    if (state) {
      setNodes(state.nodes);
      setEdges(state.edges);
      setHistoryIndex(newIndex);
    }
    setTimeout(() => {
      isUndoRedo.current = false;
    }, 100);
  }, [history, historyIndex, setNodes, setEdges]);

  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes }: { nodes: MindMapNodeType[] }) => {
      if (selectedNodes.length >= 1) {
        const node =
          (selectedNodeId &&
            selectedNodes.find((n) => n.id === selectedNodeId)) ||
          selectedNodes[0];
        setSelectedNodeId(node.id);
        setSelectedNodeType((node.type as NodeType) || "idea");
      } else {
        setSelectedNodeId(null);
        setSelectedNodeType(null);
      }
    },
    [selectedNodeId]
  );

  // Helper to sync nodes to parent
  const syncNodesToParent = useCallback(() => {
    setTimeout(() => {
      setNodes((currentNodes) => {
        onNodesChange(currentNodes as MindMapNodeType[]);
        return currentNodes;
      });
      if (!isUndoRedo.current) saveToHistory();
    }, 0);
  }, [setNodes, onNodesChange, saveToHistory]);

  // Helper to sync edges to parent
  const syncEdgesToParent = useCallback(() => {
    setTimeout(() => {
      setEdges((currentEdges) => {
        onEdgesChange(currentEdges as MindMapEdge[]);
        return currentEdges;
      });
      if (!isUndoRedo.current) saveToHistory();
    }, 0);
  }, [setEdges, onEdgesChange, saveToHistory]);

  const getNodeDimensions = useCallback((node: MindMapNodeType) => {
    const type = (node.type as NodeType) || "idea";
    const fallback = {
      topic: { w: 120, h: 56 },
      idea: { w: 100, h: 48 },
      note: { w: 80, h: 44 },
      goal: { w: 110, h: 52 },
      step: { w: 88, h: 44 },
      category: { w: 120, h: 52 },
      break: { w: 1, h: 1 },
      junction: { w: 1, h: 1 },
    } as const;
    const w =
      (node.data as { width?: number } | undefined)?.width ??
      (node as unknown as { width?: number }).width ??
      fallback[type]?.w ??
      100;
    const h =
      (node.data as { height?: number } | undefined)?.height ??
      (node as unknown as { height?: number }).height ??
      fallback[type]?.h ??
      48;
    return { w, h };
  }, []);

  const getNodeBounds = useCallback(
    (node: MindMapNodeType) => {
      const dims = getNodeDimensions(node);
      const left = node.position.x;
      const top = node.position.y;
      return {
        left,
        right: left + dims.w,
        top,
        bottom: top + dims.h,
        centerX: left + dims.w / 2,
        centerY: top + dims.h / 2,
        w: dims.w,
        h: dims.h,
      };
    },
    [getNodeDimensions]
  );

  const clearAlignmentGuides = useCallback(() => {
    setAlignmentGuides({ x: [], y: [] });
  }, []);

  const flowToScreen = useCallback(
    (point: { x: number; y: number }) => {
      const viewport = getViewport();
      return {
        x: point.x * viewport.zoom + viewport.x,
        y: point.y * viewport.zoom + viewport.y,
      };
    },
    [getViewport]
  );

  // Allow bend-handle dragging to update controlled edges state.
  useEffect(() => {
    const handler = (evt: Event) => {
      const e = evt as CustomEvent<{
        edgeId: string;
        manualPoints: Array<{ x: number; y: number }>;
        commit?: boolean;
      }>;
      const edgeId = e.detail?.edgeId;
      const manualPoints = e.detail?.manualPoints;
      if (!edgeId || !Array.isArray(manualPoints)) return;

      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.id !== edgeId) return edge;
          return {
            ...edge,
            data: {
              ...(edge.data as Record<string, unknown>),
              manualPoints,
            },
          };
        })
      );

      if (e.detail?.commit) {
        syncEdgesToParent();
      }
    };

    window.addEventListener("mindmap:edgebends", handler as EventListener);
    return () => {
      window.removeEventListener("mindmap:edgebends", handler as EventListener);
    };
  }, [setEdges, syncEdgesToParent]);

  // Migrate legacy "break" nodes to hidden "junction" nodes and convert
  // connected edges to "broken" segments so the break appears on the line.
  useEffect(() => {
    if (migratedBreaks.current) return;
    migratedBreaks.current = true;

    const legacyIds = nodes
      .filter((n) => (n.type as NodeType) === "break")
      .map((n) => n.id);
    if (legacyIds.length === 0) return;

    const legacySet = new Set(legacyIds);

    setNodes((nds) =>
      nds.map((n) =>
        legacySet.has(n.id)
          ? {
              ...n,
              type: "junction",
              data: { ...n.data, label: "" },
              draggable: false,
              selectable: false,
              connectable: false,
            }
          : n
      )
    );

    setEdges((eds) => {
      const next = eds.map((e) => {
        const targetIs = legacySet.has(e.target);
        const sourceIs = legacySet.has(e.source);
        if (!targetIs && !sourceIs) return e;
        return {
          ...e,
          type: "broken",
          data: {
            ...(e.data as Record<string, unknown>),
            segment: targetIs ? "into" : "out",
          },
        };
      });

      // Ensure exactly one break symbol per junction by marking one incoming edge.
      for (const id of legacyIds) {
        const incoming = next.filter((e) => e.target === id && e.source !== id);
        if (incoming.length === 0) continue;
        const chosen = incoming[0];
        chosen.data = {
          ...(chosen.data as Record<string, unknown>),
          showBreak: true,
        };
      }

      return next;
    });

    syncNodesToParent();
    syncEdgesToParent();
  }, [nodes, setNodes, setEdges, syncNodesToParent, syncEdgesToParent]);

  const selectedNode = useMemo(
    () => (selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : null),
    [nodes, selectedNodeId]
  );

  const selectedAssociations = useMemo(() => {
    const raw = (selectedNode?.data?.associations ||
      []) as MindMapNodeType["data"]["associations"];
    return (raw || []).slice();
  }, [selectedNode]);

  const openLinks = useCallback(() => {
    if (!selectedNodeId) return;
    setLinksOpen(true);
  }, [selectedNodeId]);

  const closeLinks = useCallback(() => {
    setLinksOpen(false);
    setLinksQuery("");
  }, []);

  const loadLinkData = useCallback(async () => {
    if (linksLoadedOnce) return;
    setLinksLoading(true);
    try {
      const [goals, debts, expenses, income, housing, taxProfiles] =
        await Promise.all([
          goalAPI.getAll(),
          debtAPI.getAll(),
          expenseAPI.getAll(),
          incomeAPI.getAll(),
          housingForecastProfilesDB.getAll(),
          taxProfilesDB.getAll(),
        ]);
      setAvailableGoals(goals || []);
      setAvailableDebts(debts || []);
      setAvailableExpenses(expenses || []);
      setAvailableIncome(income || []);
      setAvailableHousing(housing || []);
      setAvailableTaxProfiles(taxProfiles || []);
      setLinksLoadedOnce(true);
      console.log("[MindMap] Loaded linkable records:", {
        goals: goals?.length || 0,
        debts: debts?.length || 0,
        expenses: expenses?.length || 0,
        income: income?.length || 0,
        housing: housing?.length || 0,
      });
    } catch (error) {
      console.error("[MindMap] Failed to load linkable records:", error);
    } finally {
      setLinksLoading(false);
    }
  }, [linksLoadedOnce]);

  useEffect(() => {
    if (!linksOpen) return;
    void loadLinkData();
  }, [linksOpen, loadLinkData]);

  const normalize = (value: string) => value.trim().toLowerCase();
  const query = normalize(linksQuery);

  const isLinked = useCallback(
    (
      kind: "goal" | "debt" | "expense" | "income" | "housingForecast",
      id: string
    ) => selectedAssociations.some((a) => a.kind === kind && a.id === id),
    [selectedAssociations]
  );

  const addAssociation = useCallback(
    (
      kind: "goal" | "debt" | "expense" | "income" | "housingForecast",
      id: string,
      label: string,
      signedAmount?: number,
      extra?: Partial<
        NonNullable<MindMapNodeType["data"]["associations"]>[number]
      >
    ) => {
      if (!selectedNodeId) return;
      const trimmed = label.trim();
      const next = [
        ...selectedAssociations,
        { kind, id, label: trimmed || id, signedAmount, ...(extra || {}) },
      ].filter(
        (a, index, arr) =>
          arr.findIndex((b) => b.kind === a.kind && b.id === a.id) === index
      );
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== selectedNodeId) return n;

          const nextData: MindMapNodeType["data"] = {
            ...n.data,
            associations: next,
          };

          // If a Mind Map "goal" node is linked to a Goal record, mirror its fields
          // and prevent local overrides.
          if (kind === "goal" && (n.type as NodeType) === "goal") {
            const g = (availableGoals || []).find((x) => x.id === id);
            if (g) {
              nextData.label = g.name;
              nextData.description = g.description || "";
              nextData.goalAmount = g.targetAmount;
              nextData.lockedFromGoalId = id;
            }
          }

          return { ...n, data: nextData };
        })
      );
      syncNodesToParent();
    },
    [
      availableGoals,
      selectedAssociations,
      selectedNodeId,
      setNodes,
      syncNodesToParent,
    ]
  );

  const removeAssociation = useCallback(
    (
      kind: "goal" | "debt" | "expense" | "income" | "housingForecast",
      id: string
    ) => {
      if (!selectedNodeId) return;
      const next = selectedAssociations.filter(
        (a) => !(a.kind === kind && a.id === id)
      );

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== selectedNodeId) return n;
          const nextData: MindMapNodeType["data"] = {
            ...n.data,
            associations: next,
          };

          if ((n.type as NodeType) === "goal") {
            const goalIds = next
              .filter((a) => a.kind === "goal")
              .map((a) => a.id);

            if (goalIds.length === 0) {
              delete (nextData as { lockedFromGoalId?: string })
                .lockedFromGoalId;
            } else if (
              !nextData.lockedFromGoalId ||
              !goalIds.includes(nextData.lockedFromGoalId)
            ) {
              const newLock = goalIds[0];
              const g = (availableGoals || []).find((x) => x.id === newLock);
              if (g) {
                nextData.label = g.name;
                nextData.description = g.description || "";
                nextData.goalAmount = g.targetAmount;
              }
              nextData.lockedFromGoalId = newLock;
            }
          }

          return { ...n, data: nextData };
        })
      );

      syncNodesToParent();
    },
    [
      availableGoals,
      selectedAssociations,
      selectedNodeId,
      setNodes,
      syncNodesToParent,
    ]
  );

  const renderAssociationsSummary = useCallback(() => {
    const counts = selectedAssociations.reduce(
      (acc, a) => {
        acc[a.kind] += 1;
        return acc;
      },
      { goal: 0, debt: 0, expense: 0, income: 0, housingForecast: 0 } as Record<
        "goal" | "debt" | "expense" | "income" | "housingForecast",
        number
      >
    );
    const parts = (Object.keys(counts) as Array<keyof typeof counts>)
      .filter((k) => counts[k] > 0)
      .map((k) => `${k}:${counts[k]}`);
    if (parts.length === 0) return null;
    return (
      <Typography
        variant="caption"
        sx={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)" }}
      >
        Links: {parts.join("  ")}
      </Typography>
    );
  }, [selectedAssociations, isDark]);

  const filteredGoals = useMemo(() => {
    const list = availableGoals || [];
    if (!query) return list;
    return list.filter((g) => normalize(g.name || "").includes(query));
  }, [availableGoals, query]);

  const filteredDebts = useMemo(() => {
    const list = availableDebts || [];
    if (!query) return list;
    return list.filter((d) => {
      const label = `${d.creditor || ""} ${d.account || ""}`.trim();
      return normalize(label).includes(query);
    });
  }, [availableDebts, query]);

  const filteredExpenses = useMemo(() => {
    const list = availableExpenses || [];
    if (!query) return list;
    return list.filter((e) =>
      normalize(`${e.name || ""} ${e.vendor || ""}`.trim()).includes(query)
    );
  }, [availableExpenses, query]);

  const filteredIncome = useMemo(() => {
    const list = availableIncome || [];
    if (!query) return list;
    return list.filter((i) =>
      normalize(
        `${i.client || ""} ${i.paymentProcessor || ""}`.trim()
      ).includes(query)
    );
  }, [availableIncome, query]);

  const filteredHousing = useMemo(() => {
    const list = availableHousing || [];
    if (!query) return list;
    return list.filter((p) => normalize(p.name || "").includes(query));
  }, [availableHousing, query]);

  const computeHousingNet = useCallback(
    (
      profile: { id: string; name: string; settings: any },
      excludeCapitalGainsTax: boolean
    ) => {
      const settings = profile.settings || {};
      const saleInputs = settings.saleInputs || {};
      const assets = Array.isArray(settings.assets) ? settings.assets : [];
      const selectedAssetId = settings.selectedAssetId;
      const selectedAsset =
        assets.find((a: any) => a?.id === selectedAssetId) || assets[0] || {};

      const purchasePrice = Number(selectedAsset?.purchasePrice || 0);
      const improvements = Number(selectedAsset?.improvements || 0);

      const taxProfileId = settings.taxProfileId || null;
      const taxProfile = (availableTaxProfiles || []).find(
        (t) => t.id === taxProfileId
      );
      const incomeInfo = taxProfile?.incomeInfo;
      const filingStatus =
        (incomeInfo?.filingStatus as any) || ("married_jointly" as const);
      const yearly = incomeInfo
        ? calculateIncomeDetails(incomeInfo).yearlyIncome
        : null;
      const baseGrossIncome = yearly ? yearly.grossIncome : 0;
      const baseTaxableIncome = yearly ? yearly.taxableIncome : 0;

      const salePrice = Number(saleInputs.salePrice || 0);

      const brokerCommissionPct = Number(saleInputs.brokerCommissionPct);
      const brokerCommission =
        Number.isFinite(brokerCommissionPct) && brokerCommissionPct > 0
          ? Math.max(5000, (salePrice * brokerCommissionPct) / 100)
          : Number(saleInputs.brokerCommission || 0);

      const closingPctByCategory = saleInputs.closingCostsPctByCategory;
      const closingPctTotal =
        closingPctByCategory && typeof closingPctByCategory === "object"
          ? Object.values(closingPctByCategory).reduce(
              (sum: number, v: any) => sum + (Number(v) || 0),
              0
            )
          : 0;
      const closingCosts =
        closingPctTotal > 0
          ? Math.max(0, (salePrice * closingPctTotal) / 100)
          : Number(saleInputs.closingCosts || 0);

      const analysis = calculateHousingSaleAnalysis({
        filingStatus,
        baseTaxableIncome,
        baseGrossIncome,
        salePrice,
        brokerCommission,
        closingCosts,
        otherSellingCosts: Number(saleInputs.otherSellingCosts || 0),
        purchasePrice,
        improvements,
        currentMortgageBalance: Number(saleInputs.currentMortgageBalance || 0),
        mortgageInterest: Number(saleInputs.mortgageInterest || 0),
        reinvest1031: Boolean(saleInputs.reinvest1031),
        includeNIIT: Boolean(saleInputs.includeNIIT),
      });

      return {
        netCash: excludeCapitalGainsTax
          ? analysis.netCashAfterMortgageNoTax
          : analysis.netCashAfterMortgage,
        netCashAfterMortgage: analysis.netCashAfterMortgage,
        netCashAfterMortgageNoTax: analysis.netCashAfterMortgageNoTax,
        totalTax: analysis.totalTax,
      };
    },
    [availableTaxProfiles]
  );

  const setHousingExcludeTaxForLinked = useCallback(
    (housingId: string, value: boolean) => {
      if (!selectedNodeId) return;
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== selectedNodeId) return n;
          const list = (n.data.associations || []) as NonNullable<
            MindMapNodeType["data"]["associations"]
          >;
          const next = list.map((a) => {
            if (a.kind !== "housingForecast" || a.id !== housingId) return a;
            return { ...a, excludeCapitalGainsTax: value };
          });
          return { ...n, data: { ...n.data, associations: next } };
        })
      );
      syncNodesToParent();
    },
    [selectedNodeId, setNodes, syncNodesToParent]
  );

  const handleTypeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, nextType: NodeType | null) => {
      if (!selectedNodeId || !nextType) return;
      setSelectedNodeType(nextType);
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedNodeId ? { ...node, type: nextType } : node
        )
      );
      syncNodesToParent();
    },
    [selectedNodeId, setNodes, syncNodesToParent]
  );

  const handleNodesDelete = useCallback(
    (deleted: Node[]) => {
      const ids = new Set(deleted.map((n) => n.id));
      console.log("[MindMap] onNodesDelete:", {
        count: ids.size,
        ids: [...ids],
      });
      setNodes((nds) => nds.filter((n) => !ids.has(n.id)));
      setEdges((eds) =>
        eds.filter((e) => !ids.has(e.source) && !ids.has(e.target))
      );
      syncNodesToParent();
      syncEdgesToParent();
    },
    [setNodes, setEdges, syncNodesToParent, syncEdgesToParent]
  );

  const handleEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      const ids = new Set(deleted.map((e) => e.id));
      console.log("[MindMap] onEdgesDelete:", {
        count: ids.size,
        ids: [...ids],
      });
      setEdges((eds) => eds.filter((e) => !ids.has(e.id)));
      syncEdgesToParent();
    },
    [setEdges, syncEdgesToParent]
  );

  const handleReconnectStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const handleReconnect = useCallback(
    (oldEdge: MindMapEdge, newConnection: Connection) => {
      edgeUpdateSuccessful.current = true;

      const source = newConnection.source || oldEdge.source;
      const target = newConnection.target || oldEdge.target;
      if (!source || !target) return;

      setEdges((eds) => {
        const branchId = `edge-branch-${Date.now()}-${Math.random()
          .toString(16)
          .slice(2, 8)}`;
        const nextEdge: MindMapEdge = {
          ...oldEdge,
          id: branchId,
          source,
          target,
          sourceHandle: newConnection.sourceHandle || oldEdge.sourceHandle,
          targetHandle: newConnection.targetHandle || oldEdge.targetHandle,
          markerEnd: oldEdge.markerEnd || defaultMarkerEnd,
          type: oldEdge.type || "smart",
        };

        return eds.concat(nextEdge);
      });
      syncEdgesToParent();
    },
    [setEdges, defaultMarkerEnd, syncEdgesToParent]
  );

  const handleReconnectEnd = useCallback(
    (_event: MouseEvent | TouchEvent, edge: MindMapEdge) => {
      if (!edgeUpdateSuccessful.current) {
        setEdges((eds) => eds.filter((e) => e.id !== edge.id));
        syncEdgesToParent();
      }
      edgeUpdateSuccessful.current = true;
    },
    [setEdges, syncEdgesToParent]
  );

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
      // Also support Ctrl+Y for redo
      if ((e.metaKey || e.ctrlKey) && e.key === "y") {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Save initial state to history
  useEffect(() => {
    if (history.length === 0 && nodes.length > 0) {
      setHistory([{ nodes: [...nodes], edges: [...edges] }]);
      setHistoryIndex(0);
    }
  }, [nodes, edges, history.length]);

  // Track if we're syncing from parent to avoid circular updates
  const isSyncingFromParent = useRef(false);
  const prevInitialNodesRef = useRef(initialNodes);
  const prevInitialEdgesRef = useRef(initialEdges);

  // Update from parent only when initialNodes/initialEdges actually change (new reference)
  useEffect(() => {
    if (initialNodes !== prevInitialNodesRef.current) {
      isSyncingFromParent.current = true;
      setNodes(initialNodes);
      prevInitialNodesRef.current = initialNodes;
      // Reset flag after React processes the update
      requestAnimationFrame(() => {
        isSyncingFromParent.current = false;
      });
    }
  }, [initialNodes, setNodes]);

  useEffect(() => {
    if (initialEdges !== prevInitialEdgesRef.current) {
      isSyncingFromParent.current = true;
      setEdges(initialEdges);
      prevInitialEdgesRef.current = initialEdges;
      requestAnimationFrame(() => {
        isSyncingFromParent.current = false;
      });
    }
  }, [initialEdges, setEdges]);

  // Wrap internal change handlers to sync to parent
  const handleNodesChangeInternal: OnNodesChange<MindMapNodeType> = useCallback(
    (changes) => {
      onNodesChangeInternal(
        changes as Parameters<typeof onNodesChangeInternal>[0]
      );
      if (changes.every((c) => c.type === "select")) return;
      // Sync to parent after internal changes (not when syncing from parent)
      if (!isSyncingFromParent.current && !isUndoRedo.current) {
        // Use setTimeout to get the updated nodes after React processes the change
        setTimeout(() => {
          setNodes((currentNodes) => {
            onNodesChange(currentNodes as MindMapNodeType[]);
            return currentNodes;
          });
          saveToHistory();
        }, 0);
      }
    },
    [onNodesChangeInternal, onNodesChange, setNodes, saveToHistory]
  );

  const handleEdgesChangeInternal: OnEdgesChange = useCallback(
    (changes) => {
      onEdgesChangeInternal(changes);
      if (changes.every((c) => c.type === "select")) return;
      if (!isSyncingFromParent.current && !isUndoRedo.current) {
        setTimeout(() => {
          setEdges((currentEdges) => {
            onEdgesChange(currentEdges as MindMapEdge[]);
            return currentEdges;
          });
          saveToHistory();
        }, 0);
      }
    },
    [onEdgesChangeInternal, onEdgesChange, setEdges, saveToHistory]
  );

  // Handle node label changes
  useEffect(() => {
    const handleLabelChange = (e: Event) => {
      const {
        id,
        label,
        description,
        goalDate,
        goalAmount,
        stepTimeEstimateMinutes,
      } = (e as CustomEvent).detail as {
        id: string;
        label: string;
        description?: string;
        goalDate?: string;
        goalAmount?: number | null;
        stepTimeEstimateMinutes?: number | null;
      };
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  label,
                  description,
                  goalDate,
                  ...(goalAmount !== undefined
                    ? {
                        goalAmount:
                          goalAmount === null ? undefined : goalAmount,
                      }
                    : {}),
                  ...(stepTimeEstimateMinutes !== undefined
                    ? {
                        stepTimeEstimateMinutes:
                          stepTimeEstimateMinutes === null
                            ? undefined
                            : stepTimeEstimateMinutes,
                      }
                    : {}),
                },
              }
            : node
        )
      );
      syncNodesToParent();
    };

    window.addEventListener("mindmap:labelchange", handleLabelChange);
    return () => {
      window.removeEventListener("mindmap:labelchange", handleLabelChange);
    };
  }, [setNodes, syncNodesToParent]);

  // Handle node size changes
  useEffect(() => {
    const handleSizeChange = (e: Event) => {
      const { id, width, height } = (e as CustomEvent).detail;
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, width, height } }
            : node
        )
      );
      syncNodesToParent();
    };

    window.addEventListener("mindmap:sizechange", handleSizeChange);
    return () => {
      window.removeEventListener("mindmap:sizechange", handleSizeChange);
    };
  }, [setNodes, syncNodesToParent]);

  const onConnect = useCallback(
    (params: Connection) => {
      console.log("[MindMap] onConnect:", params);
      setEdges((eds) => addEdge({ ...params, type: "smoothstep" }, eds));
      syncEdgesToParent();
    },
    [setEdges, syncEdgesToParent]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData(
        "application/reactflow"
      ) as NodeType;
      if (!nodeType) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: MindMapNodeType = {
        id: generateNodeId(),
        type: nodeType,
        position,
        data: {
          label:
            nodeType === "topic"
              ? "New Topic"
              : nodeType === "goal"
                ? "New Goal"
                : nodeType === "step"
                  ? "New Step"
                  : nodeType === "category"
                    ? "New Category"
                    : nodeType === "idea"
                      ? "New Idea"
                      : "New Note",
        },
      };

      setNodes((nds) => nds.concat(newNode));
      syncNodesToParent();
    },
    [screenToFlowPosition, setNodes, syncNodesToParent]
  );

  const handleNodeDrag = useCallback(
    (_event: unknown, dragged: MindMapNodeType) => {
      markNodeDragging(dragged.id);
      if ((dragged.type as NodeType) === "junction") {
        clearAlignmentGuides();
        return;
      }

      const draggedBounds = getNodeBounds(dragged);
      const dragAnchorsX = [
        draggedBounds.left,
        draggedBounds.centerX,
        draggedBounds.right,
      ];
      const dragAnchorsY = [
        draggedBounds.top,
        draggedBounds.centerY,
        draggedBounds.bottom,
      ];

      let closestX: number | null = null;
      let closestY: number | null = null;
      let snappedX = dragged.position.x;
      let snappedY = dragged.position.y;
      let bestXDist = Number.POSITIVE_INFINITY;
      let bestYDist = Number.POSITIVE_INFINITY;

      for (const node of nodes) {
        if (node.id === dragged.id) continue;
        const candidateNodeType = (node.type as NodeType) || "idea";
        if (candidateNodeType === "junction") continue;

        const bounds = getNodeBounds(node as MindMapNodeType);
        const targetXs = [bounds.left, bounds.centerX, bounds.right];
        const targetYs = [bounds.top, bounds.centerY, bounds.bottom];

        for (const targetX of targetXs) {
          for (const anchorX of dragAnchorsX) {
            const dist = Math.abs(anchorX - targetX);
            if (dist <= SNAP_PX) {
              if (dist < bestXDist) {
                closestX = targetX;
                bestXDist = dist;
                snappedX =
                  targetX -
                  (anchorX === draggedBounds.left
                    ? 0
                    : anchorX === draggedBounds.centerX
                      ? draggedBounds.w / 2
                      : draggedBounds.w);
              }
            }
          }
        }

        for (const targetY of targetYs) {
          for (const anchorY of dragAnchorsY) {
            const dist = Math.abs(anchorY - targetY);
            if (dist <= SNAP_PX) {
              if (dist < bestYDist) {
                closestY = targetY;
                bestYDist = dist;
                snappedY =
                  targetY -
                  (anchorY === draggedBounds.top
                    ? 0
                    : anchorY === draggedBounds.centerY
                      ? draggedBounds.h / 2
                      : draggedBounds.h);
              }
            }
          }
        }
      }

      if (closestX === null && closestY === null) {
        clearAlignmentGuides();
        return;
      }

      setAlignmentGuides({
        x: closestX === null ? [] : [closestX],
        y: closestY === null ? [] : [closestY],
      });

      setNodes((nds) =>
        nds.map((node) =>
          node.id === dragged.id
            ? {
                ...node,
                position: {
                  ...node.position,
                  x: snappedX,
                  y: snappedY,
                },
              }
            : node
        )
      );
    },
    [SNAP_PX, nodes, clearAlignmentGuides, getNodeBounds, setNodes]
  );

  const handleNodeDragStop = useCallback(
    (event: React.MouseEvent, dragged: MindMapNodeType) => {
      if ((dragged.type as NodeType) === "junction") {
        clearAlignmentGuides();
        clearNodeDragging(dragged.id);
        return;
      }
      clearNodeDragging(dragged.id);

      const dropPoint = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const getNodeDims = getNodeDimensions;

      const getNodeCenter = (node: MindMapNodeType) => {
        const { w, h } = getNodeDims(node);
        return { x: node.position.x + w / 2, y: node.position.y + h / 2 };
      };

      const pointToSegment = (
        p: { x: number; y: number },
        a: { x: number; y: number },
        b: { x: number; y: number }
      ) => {
        const abx = b.x - a.x;
        const aby = b.y - a.y;
        const apx = p.x - a.x;
        const apy = p.y - a.y;
        const abLen2 = abx * abx + aby * aby;
        const t = abLen2 <= 0 ? 0 : (apx * abx + apy * aby) / abLen2;
        const clamped = Math.max(0, Math.min(1, t));
        const proj = { x: a.x + abx * clamped, y: a.y + aby * clamped };
        const dx = p.x - proj.x;
        const dy = p.y - proj.y;
        return { dist: Math.hypot(dx, dy), proj };
      };

      let best: {
        edge: MindMapEdge;
        dist: number;
        proj: { x: number; y: number };
        a: { x: number; y: number };
        b: { x: number; y: number };
      } | null = null;

      for (const e of edges) {
        // Don't insert into broken edges; they use an internal junction node.
        if (e.type === "broken") continue;
        const src = nodes.find((n) => n.id === e.source);
        const tgt = nodes.find((n) => n.id === e.target);
        if (!src || !tgt) continue;
        const a = getNodeCenter(src);
        const b = getNodeCenter(tgt);
        const res = pointToSegment(dropPoint, a, b);
        if (!best || res.dist < best.dist) {
          best = { edge: e, dist: res.dist, proj: res.proj, a, b };
        }
      }

      const SNAP_PX = 22;
      if (!best || best.dist > SNAP_PX) {
        clearAlignmentGuides();
        return;
      }

      // Insert dragged node into the edge.
      const original = best.edge;
      if (original.source === dragged.id || original.target === dragged.id) {
        clearAlignmentGuides();
        return;
      }

      const chooseHandle = (
        from: { x: number; y: number },
        to: { x: number; y: number }
      ) => {
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        if (Math.abs(dx) >= Math.abs(dy)) {
          return dx >= 0
            ? { source: "right", target: "left" }
            : { source: "left", target: "right" };
        }
        return dy >= 0
          ? { source: "bottom", target: "top" }
          : { source: "top", target: "bottom" };
      };

      const srcNode = nodes.find((n) => n.id === original.source) || null;
      const tgtNode = nodes.find((n) => n.id === original.target) || null;
      const draggedDims = getNodeDims(dragged);
      const projectedCenter = { x: best.proj.x, y: best.proj.y };
      const srcCenter = srcNode ? getNodeCenter(srcNode) : best.a;
      const tgtCenter = tgtNode ? getNodeCenter(tgtNode) : best.b;
      const leftHandles = chooseHandle(srcCenter, projectedCenter);
      const rightHandles = chooseHandle(projectedCenter, tgtCenter);

      const newEdgeA: MindMapEdge = {
        id: `${original.id}-ins-a-${dragged.id}`,
        source: original.source,
        target: dragged.id,
        type: "smoothstep",
        sourceHandle: leftHandles.source,
        targetHandle: leftHandles.target,
      };
      const newEdgeB: MindMapEdge = {
        id: `${original.id}-ins-b-${dragged.id}`,
        source: dragged.id,
        target: original.target,
        type: "smoothstep",
        sourceHandle: rightHandles.source,
        targetHandle: rightHandles.target,
      };

      setEdges((eds) => {
        const next = eds.filter((e) => e.id !== original.id);
        next.push(newEdgeA, newEdgeB);
        return next;
      });

      // Shift nodes on the "right" side to make room, and snap the dragged
      // node onto the edge.
      const deltaX = Math.max(120, draggedDims.w + 60);
      const deltaY = Math.max(120, draggedDims.h + 60);
      const insertionX = best.proj.x;
      const insertionY = best.proj.y;
      const directionRight = best.b.x >= best.a.x;
      const directionDown = best.b.y >= best.a.y;
      const isMostlyHorizontal =
        Math.abs(best.b.x - best.a.x) >= Math.abs(best.b.y - best.a.y);

      setNodes((nds) =>
        nds.map((n) => {
          if ((n.type as NodeType) === "junction") return n;

          if (n.id === dragged.id) {
            return {
              ...n,
              position: {
                x: best.proj.x - draggedDims.w / 2,
                y: best.proj.y - draggedDims.h / 2,
              },
            };
          }

          const center = getNodeCenter(n as MindMapNodeType);
          const isOnSplitSide = isMostlyHorizontal
            ? directionRight
              ? center.x > insertionX
              : center.x < insertionX
            : directionDown
              ? center.y > insertionY
              : center.y < insertionY;
          if (!isOnSplitSide) return n;
          return {
            ...n,
            position: {
              x: isMostlyHorizontal
                ? n.position.x + (directionRight ? deltaX : -deltaX)
                : n.position.x,
              y: isMostlyHorizontal
                ? n.position.y
                : n.position.y + (directionDown ? deltaY : -deltaY),
            },
          };
        })
      );

      console.log("[MindMap] Inserted existing node into edge:", {
        nodeId: dragged.id,
        edgeId: original.id,
      });

      syncNodesToParent();
      syncEdgesToParent();
      clearAlignmentGuides();
    },
    [
      edges,
      nodes,
      screenToFlowPosition,
      setEdges,
      setNodes,
      clearAlignmentGuides,
      syncEdgesToParent,
      syncNodesToParent,
    ]
  );

  // Add node when connection is dropped on empty space
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, connectionState: FinalConnectionState) => {
      console.log("[MindMap] onConnectEnd:", {
        isValid: connectionState.isValid,
        fromNodeId: connectionState.fromNode?.id || null,
        fromHandleId: connectionState.fromHandle?.id ?? null,
        fromHandleType: connectionState.fromHandle?.type ?? null,
        toNodeId: connectionState.toNode?.id || null,
        toHandleId: connectionState.toHandle?.id ?? null,
        toHandleType: connectionState.toHandle?.type ?? null,
      });

      const fromNodeId = connectionState.fromNode?.id || null;
      if (!fromNodeId) return;

      const sourceHandleId = connectionState.fromHandle?.id ?? null;
      const toNodeId = connectionState.toNode?.id || null;
      const toHandleId = connectionState.toHandle?.id ?? null;

      const inferTargetHandleId = (handleId: string | null) => {
        if (!handleId) return null;
        if (handleId === "right" || handleId.includes("right")) return "left";
        if (handleId === "left" || handleId.includes("left")) return "right";
        if (handleId === "bottom" || handleId.includes("bottom")) return "top";
        if (handleId === "top" || handleId.includes("top")) return "bottom";
        return null;
      };

      // Add edge when dropped onto a node. This also covers cases where the UI
      // "snaps" but React Flow doesn't fire onConnect for some reason.
      if (toNodeId && toNodeId !== fromNodeId) {
        const resolvedTargetHandle =
          toHandleId ?? inferTargetHandleId(sourceHandleId);
        const connection: Connection = {
          source: fromNodeId,
          target: toNodeId,
          sourceHandle: sourceHandleId,
          targetHandle: resolvedTargetHandle,
        };

        setEdges((eds) => {
          const exists = eds.some(
            (e) =>
              e.source === connection.source &&
              e.target === connection.target &&
              (e.sourceHandle || null) === connection.sourceHandle &&
              (e.targetHandle || null) === connection.targetHandle
          );
          if (exists) return eds;
          return addEdge({ ...connection, type: "smoothstep" }, eds);
        });
        syncEdgesToParent();
        return;
      }

      const eventTarget = event.target as HTMLElement | null;

      const getNodeDims = (node: MindMapNodeType) => {
        const t = (node.type as NodeType) || "idea";
        const fallback = {
          topic: { w: 120, h: 56 },
          idea: { w: 100, h: 48 },
          note: { w: 80, h: 44 },
          goal: { w: 110, h: 52 },
          step: { w: 88, h: 44 },
          category: { w: 120, h: 52 },
          break: { w: 1, h: 1 },
          junction: { w: 1, h: 1 },
        } as const;
        const w =
          (node.data as { width?: number } | undefined)?.width ??
          (node as unknown as { width?: number }).width ??
          fallback[t]?.w ??
          100;
        const h =
          (node.data as { height?: number } | undefined)?.height ??
          (node as unknown as { height?: number }).height ??
          fallback[t]?.h ??
          48;
        return { w, h };
      };

      const getNodeCenter = (node: MindMapNodeType) => {
        const { w, h } = getNodeDims(node);
        return { x: node.position.x + w / 2, y: node.position.y + h / 2 };
      };

      const pointToSegment = (
        p: { x: number; y: number },
        a: { x: number; y: number },
        b: { x: number; y: number }
      ) => {
        const abx = b.x - a.x;
        const aby = b.y - a.y;
        const apx = p.x - a.x;
        const apy = p.y - a.y;
        const abLen2 = abx * abx + aby * aby;
        const t = abLen2 <= 0 ? 0 : (apx * abx + apy * aby) / abLen2;
        const clamped = Math.max(0, Math.min(1, t));
        const proj = { x: a.x + abx * clamped, y: a.y + aby * clamped };
        const dx = p.x - proj.x;
        const dy = p.y - proj.y;
        return { dist: Math.hypot(dx, dy), proj };
      };

      const { clientX, clientY } =
        "changedTouches" in event
          ? (event as TouchEvent).changedTouches[0]
          : (event as MouseEvent);
      const dropPoint = screenToFlowPosition({ x: clientX, y: clientY });

      // Edge snapping by proximity (DOM target is unreliable during connect end).
      let best: {
        edge: MindMapEdge;
        dist: number;
        proj: { x: number; y: number };
      } | null = null;
      for (const e of edges) {
        // Don't connect-to-edge on already-broken edges; those have an internal junction.
        if (e.type === "broken") continue;
        const src = nodes.find((n) => n.id === e.source);
        const tgt = nodes.find((n) => n.id === e.target);
        if (!src || !tgt) continue;
        const a = getNodeCenter(src);
        const b = getNodeCenter(tgt);
        const res = pointToSegment(dropPoint, a, b);
        if (!best || res.dist < best.dist) {
          best = { edge: e, dist: res.dist, proj: res.proj };
        }
      }

      const SNAP_PX = 22;
      if (best && best.dist <= SNAP_PX) {
        const junctionId = generateNodeId();
        const junctionNode: MindMapNodeType = {
          id: junctionId,
          type: "junction",
          position: { x: best.proj.x, y: best.proj.y },
          data: { label: "" },
          draggable: false,
          selectable: false,
          connectable: false,
        };

        console.log("[MindMap] Insert break node on edge:", {
          edgeId: best.edge.id,
          junctionId,
          dist: best.dist,
          at: best.proj,
        });

        setNodes((nds) => nds.concat(junctionNode));
        setEdges((eds) => {
          const next = eds.filter((e) => e.id !== best!.edge.id);
          next.push(
            {
              id: `${best!.edge.id}-a`,
              source: best!.edge.source,
              target: junctionId,
              type: "broken",
              data: { segment: "into", showBreak: true },
            },
            {
              id: `${best!.edge.id}-b`,
              source: junctionId,
              target: best!.edge.target,
              type: "broken",
              data: { segment: "out" },
            },
            {
              id: `e${fromNodeId}-${junctionId}`,
              source: fromNodeId,
              target: junctionId,
              sourceHandle: sourceHandleId || undefined,
              targetHandle: inferTargetHandleId(sourceHandleId) || undefined,
              type: "smoothstep",
            }
          );
          return next;
        });
        syncNodesToParent();
        syncEdgesToParent();
        return;
      }

      const droppedOnPane = Boolean(eventTarget?.closest(".react-flow__pane"));
      if (!droppedOnPane) return;

      // Get the source node type to create the same type
      const sourceNode = nodes.find((n) => n.id === fromNodeId);
      const nodeType = (sourceNode?.type as NodeType) || "idea";
      const labelMap: Record<NodeType, string> = {
        topic: "New Topic",
        idea: "New Idea",
        note: "New Note",
        goal: "New Goal",
        step: "New Step",
        category: "New Category",
        break: "-/ /-",
        junction: "",
      };

      const id = generateNodeId();
      const newNode: MindMapNodeType = {
        id,
        type: nodeType,
        position: screenToFlowPosition({ x: clientX, y: clientY }),
        data: { label: labelMap[nodeType] },
      };

      // Map source handle to appropriate target handle on the new node.
      const targetHandleId = inferTargetHandleId(sourceHandleId) || "left";

      setNodes((nds) => nds.concat(newNode));
      setEdges((eds) =>
        eds.concat({
          id: `e${fromNodeId}-${id}`,
          source: fromNodeId,
          sourceHandle: sourceHandleId || "right",
          target: id,
          targetHandle: targetHandleId,
          type: "smoothstep",
        })
      );
      syncNodesToParent();
      syncEdgesToParent();
    },
    [
      screenToFlowPosition,
      setNodes,
      setEdges,
      syncNodesToParent,
      syncEdgesToParent,
      edges,
      nodes,
    ]
  );

  return (
    <Box
      ref={reactFlowWrapper}
      sx={{
        flex: 1,
        position: "relative",
        height: "100%",
        overflow: "hidden",
        "& .react-flow__edges": {
          zIndex: 0,
        },
        "& .react-flow__edge": {
          zIndex: 0,
        },
        "& .react-flow__nodes": {
          zIndex: 2,
        },
        "& .react-flow__node": {
          cursor: "pointer",
        },
        "& .react-flow__handle": {
          cursor: "crosshair",
        },
        "& .react-flow__edge-path": {
          stroke: edgeColor,
          strokeWidth: 2,
          // Edges render under nodes; keep them slightly faded so overlap is less noisy.
          opacity: 0.5,
        },
        "& .react-flow__edge.selected .react-flow__edge-path": {
          opacity: 1,
        },
        "& .react-flow__selection": {
          background: isDark
            ? "rgba(93, 196, 178, 0.1)"
            : "rgba(43, 179, 157, 0.1)",
          border: `1px dashed ${isDark ? "#5dc4b2" : "#2bb39d"}`,
        },
        "& .react-flow__controls": {
          borderRadius: 2,
          border: 1,
          borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          boxShadow: 1,
        },
        "& .react-flow__controls-button": {
          bgcolor: isDark ? "#2a2a2a" : "#fff",
          borderBottom: 1,
          borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          "&:hover": {
            bgcolor: isDark ? "#3a3a3a" : "#f5f5f5",
          },
          "& svg": {
            fill: isDark ? "#fff" : "#1a1a1a",
          },
        },
        "& .react-flow__minimap": {
          bgcolor: isDark ? "#1a1a1a" : "#fff",
          borderRadius: 2,
          border: 1,
          borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
        },
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edgesWithMarkers}
        onNodesChange={handleNodesChangeInternal}
        onEdgesChange={handleEdgesChangeInternal}
        onNodesDelete={handleNodesDelete}
        onEdgesDelete={handleEdgesDelete}
        onConnect={onConnect}
        onReconnect={handleReconnect}
        onReconnectStart={handleReconnectStart}
        onReconnectEnd={handleReconnectEnd}
        onConnectEnd={onConnectEnd}
        onMoveEnd={handleMoveEnd}
        onNodeDragStart={(_event, node) => markNodeDragging(node.id)}
        onNodeDragStop={handleNodeDragStop}
        onNodeDrag={handleNodeDrag}
        onSelectionChange={handleSelectionChange}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{
          type: "smart",
          markerEnd: defaultMarkerEnd,
        }}
        connectionMode={ConnectionMode.Loose}
        fitView={!window.location.search.includes("zoom=")}
        fitViewOptions={{ padding: 0.2 }}
        deleteKeyCode={["Backspace", "Delete"]}
        selectionOnDrag
        selectionMode={SelectionMode.Partial}
        selectionKeyCode="Shift"
        multiSelectionKeyCode="Shift"
        edgesReconnectable
        reconnectRadius={12}
        panOnDrag
        style={{
          backgroundColor: isDark ? "#121212" : "#f5f5f5",
        }}
      >
        <Controls />
        <Panel
          position="top-left"
          style={{
            display: "flex",
            gap: 4,
            padding: 4,
            borderRadius: 8,
            background: isDark ? "#2a2a2a" : "#fff",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Tooltip title="Undo (Ctrl+Z)">
            <span>
              <IconButton
                size="small"
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                sx={{
                  color: isDark ? "#fff" : "#1a1a1a",
                  "&.Mui-disabled": {
                    color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                  },
                }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Ctrl+Shift+Z)">
            <span>
              <IconButton
                size="small"
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                sx={{
                  color: isDark ? "#fff" : "#1a1a1a",
                  "&.Mui-disabled": {
                    color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                  },
                }}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Panel>
        {selectedNodeId && selectedNodeType && (
          <Panel
            position="top-right"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              padding: 8,
              borderRadius: 8,
              background: isDark ? "#2a2a2a" : "#fff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: isDark ? "#fff" : "#1a1a1a" }}
            >
              Node type
            </Typography>
            <ToggleButtonGroup
              value={selectedNodeType}
              exclusive
              onChange={handleTypeChange}
              size="small"
            >
              <ToggleButton value="topic">Topic</ToggleButton>
              <ToggleButton value="idea">Idea</ToggleButton>
              <ToggleButton value="note">Note</ToggleButton>
              <ToggleButton value="goal">Goal</ToggleButton>
              <ToggleButton value="step">Step</ToggleButton>
              <ToggleButton value="category">Category</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LinkIcon fontSize="small" />}
                onClick={openLinks}
              >
                Links
              </Button>
            </Box>
            {renderAssociationsSummary()}
          </Panel>
        )}
        <Dialog open={linksOpen} onClose={closeLinks} maxWidth="md" fullWidth>
          <DialogTitle>
            Links
            {selectedNode?.data?.label ? ` - ${selectedNode.data.label}` : ""}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
              <Tabs
                value={linksTab}
                onChange={(_, next) => setLinksTab(next)}
                textColor="inherit"
                indicatorColor="primary"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="goal" label="Goals" />
                <Tab value="debt" label="Debts" />
                <Tab value="expense" label="Expenses" />
                <Tab value="income" label="Income" />
                <Tab value="housingForecast" label="Housing Forecast" />
              </Tabs>
              <Box sx={{ flex: 1 }} />
              <TextField
                size="small"
                placeholder="Search"
                value={linksQuery}
                onChange={(e) => setLinksQuery(e.target.value)}
                sx={{ minWidth: 240 }}
              />
            </Box>

            {selectedAssociations.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                {selectedAssociations.map((a) => (
                  <Chip
                    key={`${a.kind}:${a.id}`}
                    label={`${a.kind}: ${a.label}`}
                    size="small"
                    onDelete={() => removeAssociation(a.kind, a.id)}
                  />
                ))}
              </Box>
            )}

            {linksLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box sx={{ maxHeight: 520, overflow: "auto" }}>
                {linksTab === "goal" &&
                  filteredGoals.slice(0, 200).map((g) => {
                    const linked = isLinked("goal", g.id);
                    return (
                      <Box
                        key={g.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1,
                          borderBottom: isDark
                            ? "1px solid rgba(255,255,255,0.08)"
                            : "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Typography variant="body2">{g.name}</Typography>
                        {linked ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => removeAssociation("goal", g.id)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => addAssociation("goal", g.id, g.name)}
                          >
                            Add
                          </Button>
                        )}
                      </Box>
                    );
                  })}

                {linksTab === "debt" &&
                  filteredDebts.slice(0, 200).map((d) => {
                    const label = `${d.creditor || "Unknown"} > ${d.account}`;
                    const linked = isLinked("debt", d.id);
                    return (
                      <Box
                        key={d.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1,
                          borderBottom: isDark
                            ? "1px solid rgba(255,255,255,0.08)"
                            : "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Typography variant="body2">{label}</Typography>
                        {linked ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => removeAssociation("debt", d.id)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              addAssociation("debt", d.id, label, -d.balance)
                            }
                          >
                            Add
                          </Button>
                        )}
                      </Box>
                    );
                  })}

                {linksTab === "expense" &&
                  filteredExpenses.slice(0, 200).map((e) => {
                    const label = `${e.name}${e.vendor ? ` (${e.vendor})` : ""}`;
                    const linked = isLinked("expense", e.id);
                    return (
                      <Box
                        key={e.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1,
                          borderBottom: isDark
                            ? "1px solid rgba(255,255,255,0.08)"
                            : "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Typography variant="body2">{label}</Typography>
                        {linked ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => removeAssociation("expense", e.id)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              addAssociation("expense", e.id, label, -e.amount)
                            }
                          >
                            Add
                          </Button>
                        )}
                      </Box>
                    );
                  })}

                {linksTab === "income" &&
                  filteredIncome.slice(0, 200).map((i) => {
                    const label = i.client || "Unnamed";
                    const linked = isLinked("income", i.id);
                    return (
                      <Box
                        key={i.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1,
                          borderBottom: isDark
                            ? "1px solid rgba(255,255,255,0.08)"
                            : "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Typography variant="body2">{label}</Typography>
                        {linked ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => removeAssociation("income", i.id)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              addAssociation(
                                "income",
                                i.id,
                                label,
                                i.invoiceTotal
                              )
                            }
                          >
                            Add
                          </Button>
                        )}
                      </Box>
                    );
                  })}

                {linksTab === "housingForecast" &&
                  filteredHousing.slice(0, 200).map((p) => {
                    const linked = isLinked("housingForecast", p.id);
                    const exclude = Boolean(housingExcludeTaxById[p.id]);
                    const net = computeHousingNet(p, exclude);
                    const linkedAssoc = selectedAssociations.find(
                      (a) => a.kind === "housingForecast" && a.id === p.id
                    );
                    const linkedExclude = Boolean(
                      linkedAssoc?.excludeCapitalGainsTax
                    );
                    const displayNet =
                      linked && linkedAssoc
                        ? linkedExclude
                          ? linkedAssoc.housingNetCashNoTax
                          : linkedAssoc.housingNetCash
                        : net.netCash;
                    return (
                      <Box
                        key={p.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1,
                          borderBottom: isDark
                            ? "1px solid rgba(255,255,255,0.08)"
                            : "1px solid rgba(0,0,0,0.06)",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {p.name || "Unnamed"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark
                                ? "rgba(255,255,255,0.55)"
                                : "rgba(0,0,0,0.55)",
                              display: "block",
                            }}
                          >
                            Net:{" "}
                            {new Intl.NumberFormat(undefined, {
                              style: "currency",
                              currency: "USD",
                              maximumFractionDigits: 2,
                            }).format(Number(displayNet || 0))}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          {linked ? (
                            <Tooltip title="Exclude capital gains taxes from net">
                              <Checkbox
                                checked={linkedExclude}
                                onChange={(e) =>
                                  setHousingExcludeTaxForLinked(
                                    p.id,
                                    e.target.checked
                                  )
                                }
                                size="small"
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Exclude capital gains taxes from net">
                              <Checkbox
                                checked={exclude}
                                onChange={(e) =>
                                  setHousingExcludeTaxById((prev) => ({
                                    ...prev,
                                    [p.id]: e.target.checked,
                                  }))
                                }
                                size="small"
                              />
                            </Tooltip>
                          )}

                          {linked ? (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() =>
                                removeAssociation("housingForecast", p.id)
                              }
                            >
                              Remove
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                const computed = computeHousingNet(p, exclude);
                                addAssociation(
                                  "housingForecast",
                                  p.id,
                                  p.name || "Housing Forecast",
                                  undefined,
                                  {
                                    housingNetCash:
                                      computed.netCashAfterMortgage,
                                    housingNetCashNoTax:
                                      computed.netCashAfterMortgageNoTax,
                                    housingCapitalGainsTax: computed.totalTax,
                                    excludeCapitalGainsTax: exclude,
                                  }
                                );
                              }}
                            >
                              Add
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLinks}>Close</Button>
          </DialogActions>
        </Dialog>
        <MiniMap
          nodeColor={(node) => {
            switch (node.type) {
              case "topic":
                return isDark ? "#5dc4b2" : "#2bb39d";
              case "idea":
                return isDark ? "#6b8afd" : "#4f6ef7";
              case "note":
                return isDark ? "#f59e0b" : "#d97706";
              case "goal":
                return isDark ? "#f97316" : "#ea580c";
              case "step":
                return isDark ? "#facc15" : "#eab308";
              case "category":
                return isDark ? "#a855f7" : "#7c3aed";
              case "break":
                return isDark ? "#f87171" : "#ef4444";
              case "junction":
                return "transparent";
              default:
                return isDark ? "#5dc4b2" : "#2bb39d";
            }
          }}
          maskColor={isDark ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.8)"}
        />
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}
        />
        {alignmentGuides.x.map((xCoord) => {
          const linePos = flowToScreen({ x: xCoord, y: 0 }).x;
          return (
            <Box
              key={`align-x-${xCoord}`}
              sx={{
                pointerEvents: "none",
                position: "absolute",
                left: `${linePos}px`,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: isDark
                  ? "rgba(255, 0, 0, 0.75)"
                  : "rgba(255, 0, 0, 0.75)",
                zIndex: 3,
                opacity: 0.55,
              }}
            />
          );
        })}
        {alignmentGuides.y.map((yCoord) => {
          const linePos = flowToScreen({ x: 0, y: yCoord }).y;
          return (
            <Box
              key={`align-y-${yCoord}`}
              sx={{
                pointerEvents: "none",
                position: "absolute",
                left: 0,
                right: 0,
                top: `${linePos}px`,
                height: 2,
                backgroundColor: isDark
                  ? "rgba(255, 0, 0, 0.75)"
                  : "rgba(255, 0, 0, 0.75)",
                zIndex: 3,
                opacity: 0.55,
              }}
            />
          );
        })}
      </ReactFlow>
    </Box>
  );
};

export default MindMapCanvas;
